import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  COST_UNITS_MISMATCH_EVENT,
  isCostUnitsBlocked,
} from '../utils/costUnitsGuard';

/**
 * MONEY-MINOR-REBUILD A7 — full-screen block shown when the API reports a
 * cost-unit contract different from what this build expects (stale client).
 * Rendering money would be 100× wrong, so we block the whole UI and force a
 * reload. Mounted once at the app root.
 */
const CostUnitsMismatchGuard: React.FC = () => {
  const [blocked, setBlocked] = useState(isCostUnitsBlocked());

  useEffect(() => {
    const onMismatch = () => setBlocked(true);
    window.addEventListener(COST_UNITS_MISMATCH_EVENT, onMismatch);
    // Catch a mismatch that fired before this component mounted.
    if (isCostUnitsBlocked()) setBlocked(true);
    return () => window.removeEventListener(COST_UNITS_MISMATCH_EVENT, onMismatch);
  }, []);

  if (!blocked) return null;

  return (
    <Box
      role="alertdialog"
      aria-modal="true"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        bgcolor: 'rgba(15, 17, 21, 0.92)',
        color: 'common.white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 4,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
        New version available
      </Typography>
      <Typography sx={{ maxWidth: 420, mb: 3, opacity: 0.85 }}>
        This page is out of date and would display prices incorrectly. Please reload
        to get the latest version.
      </Typography>
      <Button variant="contained" size="large" onClick={() => window.location.reload()}>
        Reload now
      </Button>
    </Box>
  );
};

export default CostUnitsMismatchGuard;
